<template>
  <Combobox v-model="selected" multiple>
    <div class="relative mt-1">
      <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white text-right">
        الفروع
      </label>

      <div
        class="relative w-full cursor-default overflow-hidden rounded-lg bg-white dark:bg-gray-700 text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm"
      >
        <ComboboxInput
          ref="comboboxInput"
          class="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary focus:border-primary block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary dark:focus:border-primary disabled:bg-white dark:disabled:bg-gray-700 disabled:cursor-default focus:shadow-xl transition-all"
          :class="{ 'pl-16': selected.length > 0 }"
          :displayValue="getDisplayValue"
          @change="query = $event.target.value"
          @click="handleInputClick"
          @blur="handleBlur"
          :placeholder="isFocused ? 'اكتب للبحث...' : 'اختر الفروع'"
        />
        <button
          v-if="selected.length > 0"
          type="button"
          @click.stop="selected = []"
          class="absolute inset-y-0 left-8 flex items-center pl-2 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          title="مسح الاختيار"
        >
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <ComboboxButton
          ref="comboboxButton"
          class="absolute inset-y-0 left-0 flex items-center pl-2"
        >
          <ChevronUpDownIcon class="h-5 w-5 text-gray-400 dark:text-gray-300" aria-hidden="true" />
        </ComboboxButton>
      </div>
      <TransitionRoot
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        @after-leave="query = ''"
      >
        <ComboboxOptions
          class="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black/5 dark:ring-gray-600 focus:outline-none sm:text-sm z-[999]"
        >
          <div
            v-if="filteredBranches.length === 0 && query !== ''"
            class="relative cursor-default select-none px-4 py-2 text-gray-700 dark:text-gray-300"
          >
            لا يوجد نتائج
          </div>

          <ComboboxOption
            v-for="branch in filteredBranches"
            :key="branch.id"
            as="template"
            :value="branch.id"
            v-slot="{ selected, active }"
          >
            <li
              class="relative cursor-default select-none py-2 pl-10 pr-4 text-right"
              :class="{
                'bg-primary text-white': active,
                'text-gray-900 dark:text-white': !active
              }"
            >
              <span
                class="block truncate"
                :class="{ 'font-medium': selected, 'font-normal': !selected }"
              >
                {{ branch.display_name }}
              </span>
              <span
                v-if="selected"
                class="absolute inset-y-0 left-0 flex items-center pl-3"
                :class="{ 'text-white': active, 'text-teal-600 dark:text-teal-400': !active }"
              >
                <CheckIcon class="h-5 w-5" aria-hidden="true" />
              </span>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </TransitionRoot>
    </div>
  </Combobox>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot
} from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import type { Branch } from '@/types/global'

const props = defineProps<{
  branches: Branch[]
}>()

const selected = defineModel<number[]>({
  default: () => []
})

let query = ref('')
const isFocused = ref(false)
const comboboxButton = ref<any>(null)
const comboboxInput = ref<any>(null)
const isInitializing = ref(true)

// Allow initial value to be set
setTimeout(() => {
  isInitializing.value = false
}, 0)

function handleInputClick() {
  isFocused.value = true
  query.value = ''
  // Trigger the ComboboxButton to open the dropdown
  if (comboboxButton.value) {
    comboboxButton.value.$el.click()
  }
}

function handleBlur() {
  isFocused.value = false
}

// Watch for selection changes and blur the input
watch(selected, () => {
  // Don't blur on initial load
  if (isInitializing.value) return

  if (comboboxInput.value && comboboxInput.value.$el) {
    comboboxInput.value.$el.blur()
  }
  isFocused.value = false
  query.value = ''
})

function getDisplayValue() {
  // When focused and user is typing, show the query
  if (isFocused.value) {
    return query.value
  }

  // When not focused, show the selected values
  if (selected.value.length === 0) {
    return ''
  }

  const selectedBranches = props.branches
    .filter((b) => selected.value.includes(b.id))
    .map((b) => b.display_name)
    .join(', ')
  return selectedBranches || ''
}

let filteredBranches = computed(() =>
  query.value === ''
    ? props.branches
    : props.branches.filter((branch) =>
        branch.display_name
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, ''))
      )
)
</script>
